import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCircleCheck } from '@fortawesome/free-solid-svg-icons'

import { track } from '../lib/analytics'
import { toExternalImage } from '../lib/dropify'
import type { StorefrontCategory } from '../lib/dropify'

export const SOCKS_LANDING_PATH = '/grosir-kaos-kaki'

const PROMO_IMAGE_TRANSFORM = {
  width: 900,
  height: 900,
  smart: true,
  format: 'webp',
  quality: 76,
} as const

const SOCKS_PROMO_IMAGE_URL =
  'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/04/30/34cdba54014b43c08ed914d76d57f544.png'

type SocksLandingPromoProps = {
  category?: StorefrontCategory | null
}

const BENEFITS = ['Untuk wanita, pria, dan anak', 'Model dibedakan per kategori', 'Order grosir via WhatsApp']

export function SocksLandingPromo({
  category = null,
}: SocksLandingPromoProps) {
  const imageUrl = toExternalImage(category?.image_url ?? SOCKS_PROMO_IMAGE_URL, PROMO_IMAGE_TRANSFORM)

  return (
    <section
      id="promo-grosir-kaos-kaki"
      aria-labelledby="socks-promo-title"
      data-secondary-section="grosir-kaos-kaki"
      className="bg-slate-50 px-4 py-10 sm:py-12"
    >
      <div className="relative mx-auto grid max-w-7xl overflow-hidden rounded-3xl bg-[#0B1120] shadow-xl shadow-slate-950/10 lg:grid-cols-[1.08fr_0.92fr]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-24 top-8 h-72 w-72 rounded-full bg-blue-600/20 blur-3xl"
        />
        <div className="relative z-10 flex flex-col justify-center px-6 py-7 sm:px-10 sm:py-10 lg:px-12 lg:py-12">
          <p className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-rose-400/40 bg-rose-500/15 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-rose-200 sm:mb-3 sm:text-xs">
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75 motion-reduce:animate-none" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-400" />
            </span>
            Pilihan kaos kaki grosir
          </p>
          <h2
            id="socks-promo-title"
            className="max-w-2xl text-2xl font-black leading-[1.05] tracking-tight text-white min-[360px]:text-3xl sm:text-4xl lg:text-5xl"
          >
            Bandingkan model kaos kaki{' '}
            <span className="text-amber-300">sebelum menentukan pesanan.</span>
          </h2>
          <p
            className="mt-3 max-w-2xl text-[13px] leading-relaxed text-slate-300 sm:mt-4 sm:text-base"
          >
            Kaos kaki wanita, pria, anak, sekolah, bola, bayi, muslimah, sampai foot
            cover. Buka halaman kategori untuk melihat perbedaan model, lalu kirim
            kode produk dan jumlah yang dibutuhkan kepada admin.
          </p>

          <ul className="mt-4 flex flex-wrap gap-x-3 gap-y-2 sm:mt-5 sm:gap-x-4">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-1.5 text-xs font-semibold text-slate-200 sm:text-sm">
                <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5 text-amber-300" />
                {benefit}
              </li>
            ))}
          </ul>

          <div className="mt-5 flex flex-wrap items-center gap-3 sm:mt-7">
            <a
              href={SOCKS_LANDING_PATH}
              onClick={() =>
                track('select_promotion', {
                  promotion_id: 'grosir-kaos-kaki',
                  promotion_name: 'Grosir Kaos Kaki',
                  creative_name: 'Promo grosir kaos kaki bulan ini',
                  creative_slot: 'after_whatsapp_channel',
                  page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
                })
              }
              className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-amber-300 px-5 py-3 text-sm font-black text-slate-950 shadow-lg shadow-amber-950/20 transition-all hover:-translate-y-0.5 hover:bg-amber-200 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              Bandingkan Model Kaos Kaki
              <FontAwesomeIcon
                icon={faArrowRight}
                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
              />
            </a>
            <p className="text-xs font-medium text-slate-400">
              Harga dan stok dikonfirmasi oleh admin
            </p>
          </div>
        </div>

        <div className="relative min-h-72 overflow-hidden sm:min-h-80 lg:min-h-full">
          <img
            src={imageUrl}
            alt="Pilihan produk grosir kaos kaki Dropify"
            width="900"
            height="900"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/10 md:bg-gradient-to-r md:from-[#0B1120]/40 md:via-transparent md:to-transparent" />
          <div className="absolute bottom-4 left-4 rounded-2xl border border-white/20 bg-slate-950/80 px-4 py-3 text-white shadow-xl backdrop-blur-md sm:bottom-6 sm:left-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-300">Panduan memilih</p>
            <p className="mt-0.5 text-xl font-black leading-none">Cocokkan model dan ukuran</p>
            <p className="mt-1 text-[11px] text-slate-300">Lanjutkan pemesanan melalui WhatsApp</p>
          </div>
          <div className="absolute right-4 top-4 rounded-full bg-white px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-900 shadow-lg sm:right-6 sm:top-6">
            Kategori kaos kaki
          </div>
        </div>
      </div>
    </section>
  )
}
